import { useLazyQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import {
  DynamicField,
  ExemptReason,
  ProgramArea,
  ReportingFacilityChemicalQuery,
  UnitType,
} from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformStateField";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useCallback } from "react";

import { getExemptValidationIssues } from "./exemptValidationIssues";
import { getReportingUnitOverrideValidationIssues } from "./reportingUnitOverrideValidationIssues";

const VALIDATE_REPORTING_FACILITY_CHEMICAL_INPUT = gql(`
  query ValidateReportingFacilityChemicalInput($id: ID!, $input: [FacilityChemicalStateFieldInput!]!) {
    reportingFacilityChemicalInputValidator(id: $id, input: $input) {
      ...issue
    }
  }
`);

export function useReportingFacilityChemicalInputValidation(
  rfc: ReportingFacilityChemicalQuery["reportingFacilityChemical"] | undefined
) {
  const [query] = useLazyQuery(VALIDATE_REPORTING_FACILITY_CHEMICAL_INPUT, {
    fetchPolicy: "no-cache",
  });
  // The override issues are staff-only: a customer cannot see the field they
  // key to, cannot edit it, and cannot clear the override that caused them.
  const { isStaff } = useCurrentUser();

  return useCallback(
    async (input: {
      stateFields: (DynamicField & { type: ProgramArea })[];
      isExempt?: boolean | null;
      isExemptReason?: ExemptReason | null;
      isExemptNote?: string | null;
      reportingUnitOverrideEnabled?: boolean | null;
      reportingUnitOverride?: UnitType | null;
      reportingUnitOverrideReason?: string | null;
    }) => {
      if (!rfc) return [];

      const transformedInput = input.stateFields?.map((sf) => ({
        ...transform(sf),
        facilityId: rfc.facilityId,
        chemicalId: rfc.chemicalId,
        reportingYear: rfc.reportingYear,
      }));

      const { data: result } = await query({
        variables: {
          id: rfc.id,
          input: transformedInput,
        },
      });
      return [
        ...(result?.reportingFacilityChemicalInputValidator ?? []),
        ...getExemptValidationIssues(input, "ReportingFacilityChemical"),
        ...getReportingUnitOverrideValidationIssues(
          input,
          "ReportingFacilityChemical",
          rfc,
          isStaff
        ),
      ];
    },
    [isStaff, query, rfc]
  );
}
